import { motion } from "framer-motion";
import { Users, UserCheck, Heart, Baby, ShieldCheck, FileText, Calendar, Briefcase, Mail, Phone } from "lucide-react";
import { Badge } from "../../../../components/ui/Badge";
import AuditField from "./AuditField";

export default function BlocCompositionFoyer({ data }) {
  const membres = data.membres || [];
  const primaryMember = membres.find(m => m.isPrimaryContact) || membres[0] || {
    nom: data.nomFamille || data.nom,
    prenom: data.prenom || "",
    role: "CHEF_DE_FAMILLE",
    profession: data.profession || "Dirigeant / Cadre",
    email: data.email || data.reference,
    telephone: data.telephone
  };
  
  const conjoint = membres.find(m => m.role === "CONJOINT" || (!m.isPrimaryContact && m.role !== "ENFANT"));
  const enfants = membres.filter(m => m.role === "ENFANT");

  return (
    <motion.section 
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* Banner Introductif Métier */}
      <div className="workspace-card p-5 rounded-[var(--hp-radius-lg)] border border-workspace bg-surface shadow-hp-sm">
        <div className="flex items-start gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-info-50 dark:bg-info-900/30 text-info-700 dark:text-info-400 flex items-center justify-center shrink-0">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-primary">Composition du Foyer Fiscal</h2>
            <p className="text-sm text-subtle mt-0.5">
              Le foyer fiscal consolide les personnes physiques sous imposition commune (Impôt sur le Revenu & IFI). 
              Le point d'entrée principal pilote les arbitrages patrimoniaux qui affectent l'ensemble de la famille.
            </p>
          </div>
        </div>
      </div>

      {/* --- CARTES DES MEMBRES DU COUPLE / DÉCLARANTS --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Déclarant 1 : Point d'entrée principal */}
        <div className="workspace-card p-5 rounded-[var(--hp-radius-lg)] border border-workspace bg-surface shadow-hp-sm relative overflow-hidden flex flex-col justify-between">
          <div className="absolute top-0 left-0 right-0 h-1 bg-[var(--hp-color-accent)]" />
          
          <div>
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[var(--hp-color-surface-muted)] flex items-center justify-center text-primary font-bold text-sm">
                  {primaryMember.prenom?.charAt(0) || primaryMember.nom?.charAt(0) || "D1"}
                </span>
                <div>
                  <h3 className="text-base font-bold text-primary">
                    {primaryMember.prenom} {primaryMember.nom}
                  </h3>
                  <p className="text-xs text-subtle">Déclarant 1</p>
                </div>
              </div>
              <Badge variant="accent" className="text-[11px] flex items-center gap-1">
                <UserCheck className="w-3.5 h-3.5" />
                Point d'Entrée Référent
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <AuditField label="Rôle Familial" value="Chef de Foyer (Décisionnaire)" />
              <AuditField label="Profession" value={primaryMember.profession || "Cadre / Dirigeant"} />
              <AuditField label="Téléphone" value={primaryMember.telephone || data.telephone || "Non renseigné"} />
              <AuditField label="Email de Contact" value={primaryMember.email || data.email || "Non renseigné"} />
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-workspace flex items-center justify-between text-xs text-subtle">
            <span className="flex items-center gap-1 text-[11px]">
              <ShieldCheck className="w-3.5 h-3.5 text-success-600" />
              Signature & Mandat Actifs
            </span>
            <span className="font-semibold text-muted">Interlocuteur Principal</span>
          </div>
        </div>

        {/* Déclarant 2 : Conjoint(e) */}
        <div className="workspace-card p-5 rounded-[var(--hp-radius-lg)] border border-workspace bg-surface shadow-hp-sm relative overflow-hidden flex flex-col justify-between">
          <div className="absolute top-0 left-0 right-0 h-1 bg-info-500" />
          
          <div>
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-info-50 dark:bg-info-900/20 text-info-700 flex items-center justify-center font-bold text-sm">
                  {conjoint?.prenom?.charAt(0) || conjoint?.nom?.charAt(0) || "D2"}
                </span>
                <div>
                  <h3 className="text-base font-bold text-primary">
                    {conjoint ? `${conjoint.prenom} ${conjoint.nom}` : "Conjoint(e) non renseigné(e)"}
                  </h3>
                  <p className="text-xs text-subtle">Déclarant 2</p>
                </div>
              </div>
              <Badge variant="info" className="text-[11px] flex items-center gap-1">
                <Heart className="w-3.5 h-3.5" />
                Conjoint Associé
              </Badge>
            </div>

            {conjoint ? (
              <div className="grid grid-cols-2 gap-3 mt-4">
                <AuditField label="Rôle Familial" value="Conjoint(e)" />
                <AuditField label="Profession" value={conjoint.profession || "Profession libérale / Salarié"} />
                <AuditField label="Protection Conjoint" value={data.protectionConjoint ? "Prévue (Clause / Assurance)" : "À auditer"} />
                <AuditField label="Donation entre Époux" value={data.donationEffectuee ? "Enregistrée" : "Non réalisée"} />
              </div>
            ) : (
              <div className="p-6 text-center text-subtle text-sm italic">
                Célibataire, divorcé(e) ou conjoint(e) non rattaché(e) fiscalement.
              </div>
            )}
          </div>

          <div className="mt-4 pt-3 border-t border-workspace flex items-center justify-between text-xs text-subtle">
            <span className="text-[11px]">Rattachement : Imposition Commune IR</span>
            <span className="font-semibold text-muted">{data.regimeMatrimonial || "Régime Légal"}</span>
          </div>
        </div>
      </div>

      {/* --- ENFANTS ET AYANTS DROIT --- */}
      <div className="workspace-card p-5 rounded-[var(--hp-radius-lg)] border border-workspace bg-surface shadow-hp-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <Baby className="w-5 h-5 text-secondary" />
            <h3 className="text-base font-bold text-primary">
              Enfants & Personnes à Charge ({enfants.length})
            </h3>
          </div>
          <Badge variant="neutral">
            {enfants.length > 0 ? `${enfants.length * 0.5 + (enfants.length > 2 ? 0.5 : 0)} demi-part(s) fiscale(s)` : "Aucun enfant à charge"}
          </Badge>
        </div>

        {enfants.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5">
            {enfants.map((enf, idx) => (
              <div key={enf.id || idx} className="p-3.5 rounded-lg border border-workspace bg-surface-muted/30 flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-surface border border-workspace flex items-center justify-center text-subtle font-bold text-xs shrink-0">
                  {enf.prenom?.charAt(0) || `E${idx + 1}`}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-primary truncate">{enf.prenom} {enf.nom}</p>
                  <p className="text-xs text-subtle mt-0.5">
                    {enf.age ? `${enf.age} ans` : "Mineur"} • Rattaché fiscalement
                  </p>
                  <span className="inline-block mt-2 text-[10px] font-semibold text-info-700 dark:text-info-300 bg-info-50 dark:bg-info-900/30 px-2 py-0.5 rounded">
                    Bénéficiaire potentiel
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-subtle italic py-2">
            Aucun enfant ou personne à charge rattaché fiscalement à ce foyer.
          </p>
        )}
      </div>

      {/* --- CADRE JURIDIQUE & MATRIMONIAL --- */}
      <div className="workspace-card p-5 rounded-[var(--hp-radius-lg)] border border-workspace bg-surface shadow-hp-sm">
        <div className="flex items-center gap-2.5 mb-4">
          <FileText className="w-5 h-5 text-secondary" />
          <h3 className="text-base font-bold text-primary">Régime Matrimonial & Dispositions Juridiques</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3.5">
          <AuditField label="Régime Matrimonial" value={data.regimeMatrimonial || "Communauté légale"} />
          <AuditField label="Contrat de Mariage" value={data.contratMariage || (data.regimeMatrimonial ? "Rédigé" : "Non")} />
          <AuditField label="Date de Mariage" value={data.dateMariage?.slice(0, 10) || "Non renseignée"} />
          <AuditField label="Famille Recomposée" value={data.familleRecomposee ? "Oui" : "Non"} />
          <AuditField label="Testament Rédigé" value={data.testament ? "Oui (Déposé chez notaire)" : "Non"} />
          <AuditField label="Protection du Conjoint" value={data.protectionConjoint ? "Oui" : "À structurer"} />
          <AuditField label="Donation entre Époux" value={data.donationEffectuee ? "Oui" : "Non"} />
          <AuditField label="Résidence Fiscale" value={data.paysResidence || "France (FR)"} />
        </div>
      </div>
    </motion.section>
  );
}
